import moment from "moment";
import { useEffect, useState } from "react";
import {
  FaArrowAltCircleDown,
  FaArrowAltCircleUp,
  FaBoxOpen,
  FaEye,
  FaPencilAlt,
} from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { Input, Table, Button, Spinner } from "reactstrap";
import imgIcon from "../../assets/svg/image-icon.png";
import { Pagination, Tooltip } from "antd";
import useRowSelection from "../../views/Admin/customHooks/useRowSelection";
import FormInput from "../formInput/FormInput";
import NotFoundDataUI from "../cards/NotFoundDataUI";
import { syncProductToQuickBooks } from "../../api/products.api";
import { saveData } from "../../firebase/utility";
import toast from "react-hot-toast";
import { SiQuickbooks } from "react-icons/si";

import DistributorStockModal from "../modals/DistributorStockModal";
import HighlightText from "../globals/HighlightText";

const StockCellInput = ({ product, user, gettingProducts }) => {
  const [value, setValue] = useState(product?.stc_quantity ?? 0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValue(product?.stc_quantity ?? 0);
  }, [product?.stc_quantity]);

  const handleSave = async () => {
    const num = Number(value);

    if (isNaN(num) || num < 0) {
      toast.error("Stock quantity must be a non-negative number.");
      setValue(product?.stc_quantity ?? 0);
      return;
    }

    const currentQty = Number(product?.stc_quantity ?? 0);
    if (num === currentQty) {
      setValue(num);
      return;
    }

    setSaving(true);
    try {
      const actorSnapshot = {
        id: user?._id ?? "",
        name: user?.username || user?.name || "Admin",
        email: user?.email ?? "",
      };

      const payload = {
        stc_quantity: num,
        updated_at: Date.now(),
        updated_by: { ...actorSnapshot, at: Date.now() },
      };

      const res = await saveData("Products", product._id, payload);
      if (res) {
        toast.success(`Stock updated to ${num} for ${product?.sku || product?.name || "product"}`);
        product.stc_quantity = num;
        product.updated_at = payload.updated_at;
        product.updated_by = payload.updated_by;
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        if (typeof gettingProducts === "function") {
          gettingProducts();
        }
      } else {
        toast.error("Failed to update stock.");
        setValue(product?.stc_quantity ?? 0);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update stock.");
      setValue(product?.stc_quantity ?? 0);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    } else if (e.key === "Escape") {
      setValue(product?.stc_quantity ?? 0);
      e.target.blur();
    }
  };

  return (
    <div className="position-relative d-inline-flex align-items-center justify-content-center">
      <input
        type="number"
        min="0"
        step="1"
        disabled={saving}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={onKeyDown}
        className={`form-control form-control-sm text-center px-1 ${saved ? "border-success bg-success-subtle" : ""}`}
        style={{
          width: "65px",
          height: "28px",
          fontSize: "12px",
          fontWeight: "500",
          transition: "all 0.2s ease",
          backgroundColor: saving ? "#f1f5f9" : undefined,
        }}
        title="Press Enter or click outside to save"
      />
      {saving && (
        <div
          className="position-absolute d-flex align-items-center justify-content-center"
          style={{ right: "-18px" }}
        >
          <Spinner size="sm" color="primary" style={{ width: "12px", height: "12px" }} />
        </div>
      )}
    </div>
  );
};

const ShelfLocationCellInput = ({ product, user, gettingProducts }) => {
  const [value, setValue] = useState(product?.shelf_location || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValue(product?.shelf_location || "");
  }, [product?.shelf_location]);

  const handleSave = async () => {
    const trimmed = (value || "").trim();
    const currentLoc = (product?.shelf_location || "").trim();

    if (trimmed === currentLoc) {
      setValue(trimmed);
      return;
    }

    setSaving(true);
    try {
      const actorSnapshot = {
        id: user?._id ?? "",
        name: user?.username || user?.name || "Admin",
        email: user?.email ?? "",
      };

      const payload = {
        shelf_location: trimmed,
        updated_at: Date.now(),
        updated_by: { ...actorSnapshot, at: Date.now() },
      };

      const res = await saveData("Products", product._id, payload);
      if (res) {
        toast.success(`Shelf placement updated to "${trimmed || "None"}" for ${product?.sku || product?.name || "product"}`);
        product.shelf_location = trimmed;
        product.updated_at = payload.updated_at;
        product.updated_by = payload.updated_by;
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        if (typeof gettingProducts === "function") {
          gettingProducts();
        }
      } else {
        toast.error("Failed to update shelf placement.");
        setValue(product?.shelf_location || "");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update shelf placement.");
      setValue(product?.shelf_location || "");
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    } else if (e.key === "Escape") {
      setValue(product?.shelf_location || "");
      e.target.blur();
    }
  };

  return (
    <div className="position-relative d-inline-flex align-items-center justify-content-center">
      <input
        type="text"
        disabled={saving}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={onKeyDown}
        placeholder="Shelf #"
        className={`form-control form-control-sm text-center px-1 ${saved ? "border-success bg-success-subtle" : ""}`}
        style={{
          width: "75px",
          height: "28px",
          fontSize: "12px",
          fontWeight: "500",
          transition: "all 0.2s ease",
          backgroundColor: saving ? "#f1f5f9" : undefined,
        }}
        title="Shelf / Placement location. Press Enter or click outside to save"
      />
      {saving && (
        <div
          className="position-absolute d-flex align-items-center justify-content-center"
          style={{ right: "-18px" }}
        >
          <Spinner size="sm" color="primary" style={{ width: "12px", height: "12px" }} />
        </div>
      )}
    </div>
  );
};

const ItemsTable = ({
  products,
  setSelectedData,
  toggleCreateModal,
  setSelectedProduct,
  user,
  searchValue,
  page,
  setPage,
  totalPages,
  pageSize,
  setPageSize,
  gettingProducts,
  onClearSearch,
}) => {
  const history = useHistory();
  const [sortOrder, setSortOrder] = useState("desc");
  const [distStockModal, setDistStockModal] = useState(false);
  const [selectedDistProduct, setSelectedDistProduct] = useState(null);

  const toggleDistStockModal = (product = null) => {
    setSelectedDistProduct(product);
    setDistStockModal(!distStockModal);
  };

  const {
    selectedRows,
    handleCheckboxChange,
    handleSelectAll,
    handleViewSelected,
  } = useRowSelection();

  const [syncingId, setSyncingId] = useState(null);

  useEffect(() => {
    const selected = handleViewSelected(products);
    if (typeof setSelectedData === "function") {
      setSelectedData((prev) => {
        const prevIds = (prev || []).map((p) => p._id).join(",");
        const nextIds = (selected || []).map((p) => p._id).join(",");
        return prevIds === nextIds ? prev : selected;
      });
    }
  }, [selectedRows, products]);

  const toggleSortOrder = () =>
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));

  const getPartNumber = (sku) =>
    sku ? sku.split("-").slice(1).join("-") : "N/A";

  const getFormattedDate = (product) =>
    moment(product?.updated_at || product?.date_created).format(
      "MMM DD, YYYY, h:mm A"
    );

  const sortedProducts = [...products].sort((a, b) => {
    const dateA = new Date(a.updated_at || a.date_created);
    const dateB = new Date(b.updated_at || b.date_created);
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });

  const handleSyncQB = (row) => {
    const confirmSync = window.confirm(
      `Are you sure you want to sync "${row.name || row.sku}" to QuickBooks?`
    );
    if (!confirmSync) return;

    setSyncingId(row._id);
    syncProductToQuickBooks(row._id, (res) => {
      if (res.status === 200) {
        toast.success("Successfully synced to QuickBooks!");
        gettingProducts();
      } else {
        toast.error(res.message || "Failed to sync to QuickBooks.");
      }
      setSyncingId(null);
    });
  };

  return (
    <>
      <DistributorStockModal
        isOpen={distStockModal}
        toggle={() => toggleDistStockModal()}
        product={selectedDistProduct}
      />
      <div>
        <Table
          responsive
          hover
          bordered
          className="mb-0"
          style={{
            fontSize: "13px",
            overflow: "hidden",
            border: "1px solid #e2e8f0"
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#00509d", color: "white" }}>
              {(user?.role === "Admin" || user?.role === "Super-admin") && (
                <th className="text-center" style={{ borderBottom: "none" }}>
                  <Input
                    type="checkbox"
                    onChange={(e) => handleSelectAll(e, products)}
                    checked={selectedRows?.length === products?.length && products?.length > 0}
                  />
                </th>
              )}

              <th className="text-center" style={{ borderBottom: "none" }}>Image</th>
              <th className="text-center" style={{ borderBottom: "none" }}>QB ID</th>
              <th className="text-center" style={{ borderBottom: "none" }}>Identifiers</th>
              <th className="text-center" style={{ width: "90px", borderBottom: "none" }}>
                MFG Part
              </th>
              <th className="text-center" style={{ width: "250px", borderBottom: "none" }}>
                Description
              </th>
              <th className="text-center" style={{ width: "80px", borderBottom: "none" }}>
                Stock
              </th>
              <th className="text-center" style={{ width: "95px", borderBottom: "none" }}>
                Shelf / Loc
              </th>
              <th className="text-center" style={{ width: "120px", borderBottom: "none" }}>
                Dist. Stock
              </th>
              <th className="text-center pointer" style={{ borderBottom: "none" }} onClick={toggleSortOrder}>
                Updated At{" "}
                {sortOrder === "asc" ? (
                  <FaArrowAltCircleUp />
                ) : (
                  <FaArrowAltCircleDown />
                )}
              </th>
              <th className="text-center" style={{ borderBottom: "none" }}>Created At</th>
              <th className="text-center" style={{ borderBottom: "none" }}>Price</th>
              <th className="text-center" style={{ borderBottom: "none" }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {sortedProducts?.length > 0 ? (
              sortedProducts.map((row, index) => (
                <tr
                  key={row._id}
                  className="align-middle"
                  style={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                    borderBottom: "1px solid #f1f5f9"
                  }}
                >
                  {(user?.role === "Admin" || user?.role === "Super-admin") && (
                    <td className="text-center">
                      <Input
                        type="checkbox"
                        checked={selectedRows.includes(row._id)}
                        onChange={(e) => handleCheckboxChange(e, row)}
                      />
                    </td>
                  )}

                  <td className="text-center">
                    <img
                      src={row.imageUrl || imgIcon}
                      height={28}
                      width={28}
                      alt="Product"
                      className="rounded border shadow-sm"
                      style={{ objectFit: 'contain', backgroundColor: 'white' }}
                    />
                  </td>

                  <td className="text-center">
                    {row.quickbooks_id ? (
                      <span className="fw-semibold text-primary">
                        <HighlightText
                          hit={row}
                          attribute="quickbooks_id"
                          fallback={row.quickbooks_id}
                          searchQuery={searchValue}
                        />
                      </span>
                    ) : (
                      <Tooltip title="Sync to QuickBooks">
                        <Button
                          color="link"
                          className="p-0 border-0"
                          disabled={syncingId === row._id}
                          onClick={() => handleSyncQB(row)}
                        >
                          {syncingId === row._id ? (
                            <Spinner size="sm" color="primary" />
                          ) : (
                            <SiQuickbooks
                              size={22}
                              style={{ color: "#2CA01C" }}
                            />
                          )}
                        </Button>
                      </Tooltip>
                    )}
                  </td>
                  <td className="text-center text-uppercase small">
                    {row.identifiers && row.identifiers.length > 0 ? (
                      row.identifiers.map((ident, i) => (
                        <span key={i}>
                          <HighlightText text={ident} searchQuery={searchValue} />
                          {i < row.identifiers.length - 1 ? ", " : ""}
                        </span>
                      ))
                    ) : (
                      <span className="text-muted">
                        <HighlightText text="techmicro" searchQuery={searchValue} />
                      </span>
                    )}
                  </td>

                  <td className="text-center">
                    <Tooltip title={row.sku || getPartNumber(row.sku)}>
                      <span className="fw-bold">
                        <HighlightText
                          hit={row}
                          attribute="item_mfg_part"
                          fallback={getPartNumber(row.sku)}
                          searchQuery={searchValue}
                        />
                      </span>
                    </Tooltip>
                  </td>
                  <td
                    style={{
                      width: "250px",
                    }}
                    className="text-center text-muted"
                  >
                    <div style={{
                      lineHeight: '1.4',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      <Tooltip title={row.description?.length > 100 ? row.description : ""}>
                        <HighlightText
                          hit={row}
                          attribute="description"
                          fallback={row.description || row.name || ""}
                          searchQuery={searchValue}
                        />
                      </Tooltip>
                    </div>
                  </td>

                  <td className="text-center">
                    <StockCellInput
                      product={row}
                      user={user}
                      gettingProducts={gettingProducts}
                    />
                  </td>

                  <td className="text-center">
                    <ShelfLocationCellInput
                      product={row}
                      user={user}
                      gettingProducts={gettingProducts}
                    />
                  </td>

                  <td className="text-center">
                    <div className="d-flex flex-column align-items-center">
                      <span className="fw-bold text-dark mb-1">
                        {Object.values(row.distributor_stock || {}).reduce((a, b) => a + (Number(b) || 0), 0)}
                      </span>
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 text-decoration-none text-info"
                        style={{ fontSize: '11px' }}
                        onClick={() => toggleDistStockModal(row)}
                      >
                        <FaBoxOpen className="me-1" />
                        Details
                      </Button>
                    </div>
                  </td>

                  <td className="text-center text-muted" style={{ fontSize: '12px' }}>
                    {(row?.updatedBy != null || row?.updated_by != null) ? (
                      <>
                        <div className="fw-bold text-dark mb-1">
                          {typeof row?.updatedBy === 'string'
                            ? row.updatedBy
                            : (
                              row?.updatedBy?.username ||
                              row?.updatedBy?.name ||
                              row?.updated_by?.username ||
                              row?.updated_by?.name ||
                              "Admin"
                            )}
                        </div>

                        {getFormattedDate(row)}
                      </>
                    ) : (
                      <div className="fw-bold text-muted">
                        Not Available
                      </div>
                    )}
                  </td>
                  <td className="text-center text-muted" style={{ fontSize: '12px' }}>
                    {moment(row?.date_created || row?.createdAt).format("M/D/YYYY")}
                  </td>

                  <td className="text-center fw-bold text-dark">
                    {row.price ? `$ ${row.price}` : "N/A"}
                  </td>

                  <td className="text-center">
                    <div className="d-flex justify-content-center gap-2">
                      <Tooltip title="Edit Product">
                        <Button
                          color="link"
                          className="p-1 border-0"
                          onClick={() => {
                            setSelectedProduct(row);
                            toggleCreateModal();
                          }}
                        >
                          <FaPencilAlt className="text-primary" size={14} />
                        </Button>
                      </Tooltip>

                      <Tooltip title="View Product Details">
                        <Button
                          color="link"
                          className="p-1 border-0"
                          onClick={() =>
                            history.push({
                              pathname: `/admin/product-details/${row._id}`,
                              state: row,
                            })
                          }
                        >
                          <FaEye className="text-info" size={18} />
                        </Button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={(user?.role === "Admin" || user?.role === "Super-admin") ? 12 : 11} className="text-center py-4">
                  <NotFoundDataUI 
                    message="Products not found!" 
                    onClearSearch={onClearSearch}
                    onCreateNew={toggleCreateModal}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {sortedProducts?.length > 0 && (
          <div className="d-flex justify-content-end mt-3 px-2">
            <Pagination
              size="small"
              current={page}
              total={totalPages}
              onChange={(value) => {
                localStorage.setItem("productPage", value);
                setPage(value);
              }}
              pageSize={pageSize}
              showSizeChanger={true}
              onShowSizeChange={(current, size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSizeOptions={['10', '25', '50', '100']}
            />
          </div>
        )}
      </div >
    </>
  );
};

export default ItemsTable;
